/**
 * The agent read panel's copy — a pure port of the prototype's `agentAdvice`/`suggestNext`.
 *
 * Judgement, not a recap of numbers already on screen: whether the working year's requirement
 * is met and by how much, whether the plan holds through later years, the cheapest next move
 * when short, whether bought capacity is actually needed, and early warnings on waivers,
 * uncertified sets, and a relocate threshold reaching into stock customers order (build
 * handover §9). Rules fire in priority order, capped at three lines.
 */

import {
  runScenario,
  type ScenarioContext,
  type ScenarioParams,
  type ScenarioResult,
} from "@/lib/data";

import { SYSTEM_BY_KEY } from "./mock";

export interface AdviceLine {
  html: string;
  dim?: boolean;
}

const fmt = (n: number) => Math.round(n).toLocaleString("en-US");

/** Cheapest next move when the working year is still short — walks each free lever forward one
 *  notch at a time and reports the first one that closes the gap. */
export function suggestNext(draft: ScenarioParams, ctx: ScenarioContext): AdviceLine {
  if (draft.reloThreshold < 0) {
    return {
      html: "Cheapest next move: switch on relocate. Dead stock alone frees real faces at no cost and no customer impact.",
      dim: true,
    };
  }
  if (draft.packCount < 10) {
    let v = draft.packCount + 1;
    while (v < 10 && runScenario({ ...draft, packCount: v }, ctx).room < 0) {
      v++;
    }
    if (runScenario({ ...draft, packCount: v }, ctx).room >= 0) {
      const n = v - draft.packCount;
      return {
        html: `Cheapest next move: <b>${n}</b> more pack ${n === 1 ? "family" : "families"} closes it${
          v >= 6 && draft.packCount < 6 ? ", though going past five triggers the waivers" : ""
        }.`,
        dim: true,
      };
    }
  }
  if (draft.convCount < 8) {
    let v = draft.convCount + 1;
    while (v < 8 && runScenario({ ...draft, convCount: v }, ctx).room < 0) {
      v++;
    }
    if (runScenario({ ...draft, convCount: v }, ctx).room >= 0) {
      const n = v - draft.convCount;
      return {
        html: `Cheapest next move: convert <b>${n}</b> more fitting ${n === 1 ? "set" : "sets"}${
          v > 4 ? ", which needs the MDR check past set four" : ""
        }.`,
        dim: true,
      };
    }
  }
  return {
    html: `The free releases are maxed out. What is left is the Commercial ask (pace the intake) or capital, where ${SYSTEM_BY_KEY.aframe.name} is the cheapest unit at <b>£${SYSTEM_BY_KEY.aframe.addCostM}m</b>.`,
    dim: true,
  };
}

function engagedNow(p: ScenarioParams): boolean {
  return (
    p.reloThreshold >= 0 ||
    p.packCount > 0 ||
    p.convCount > 0 ||
    (!!p.capSystem && p.capUnits > 0) ||
    p.pacePct > 0
  );
}

export function agentAdvice(
  draft: ScenarioParams,
  result: ScenarioResult,
  ctx: ScenarioContext,
): AdviceLine[] {
  const out: AdviceLine[] = [];

  if (!engagedNow(draft)) {
    out.push({
      html: `The ${draft.year} intake does not fit as planned: the site needs <b>${fmt(-result.room)}</b> more slots than it has. That is coverable without spending — start with the free levers.`,
    });
    out.push({
      html: "Start with relocate, it has no customer impact. Or let Agent Resolve pick the smallest mix.",
      dim: true,
    });
    return out;
  }

  if (result.room < 0) {
    out.push({
      html: `Not there yet: still <b class="red-t">${fmt(-result.room)}</b> short of the ${draft.year} requirement.`,
    });
    out.push(suggestNext(draft, ctx));
  } else {
    const nextYears = ctx.model.years?.filter((y) => y > draft.year) ?? [];
    const holds = nextYears.every((y) => (result.roomByYear[y] ?? 0) >= 0);
    out.push({
      html: `The ${draft.year} requirement is met with <b class="ok-t">${fmt(result.room)}</b> to spare${
        holds
          ? ", and the plan holds through the rest of the horizon."
          : ", but a later year does not fit. This buys time, not a fix."
      }`,
    });
  }

  if (result.addFaces > 0 && draft.capSystem) {
    const system = SYSTEM_BY_KEY[draft.capSystem];
    const withoutCap = runScenario({ ...draft, capSystem: null, capUnits: 0 }, ctx).room;
    if (withoutCap >= 0) {
      out.push({
        html: `The <b>£${(draft.capUnits * system.addCostM).toFixed(1)}m</b> of ${system.name} is not needed for ${draft.year} — the releases cover it on their own. Keep it only if the goal is headroom beyond <b>+${fmt(withoutCap)}</b>.`,
        dim: true,
      });
    } else {
      out.push({
        html: `The ${system.name} is doing real work: drop it and the draft is <b>${fmt(-withoutCap)}</b> short. Its ${system.addLeadMonths}-month lead time is the critical path, so the order would need to go in now.`,
        dim: true,
      });
    }
  } else if (result.room >= 0 && result.released > 0) {
    out.push({
      html: "No capital in this plan — capital avoided against the buy-capacity route.",
      dim: true,
    });
  }

  if (draft.pacePct > 0) {
    out.push({
      html: `The deferral is Commercial's call, not ours — it moves the problem into later years rather than solving it.`,
      dim: true,
    });
  }
  if (result.waiverCount > 0) {
    out.push({
      html: "Families six and seven sit on contracted ranges: two named accounts must waive first, on 60-day notice. Worth starting those conversations now.",
      dim: true,
    });
  }
  if (result.uncertifiedSetsEngaged) {
    out.push({
      html: "Sets five and up are not yet EU MDR certified — that part of the conversion cannot be committed until certification lands.",
      dim: true,
    });
  }
  if (result.bBandWarning) {
    out.push({
      html: "The relocate threshold now reaches into B, stock customers actually order. Expect service questions; 11 picks a year is the safe stop.",
      dim: true,
    });
  }
  return out.slice(0, 3);
}
